// O mapeamento entidade ↔ tabela, num lugar só.
//
// Antes, adicionar uma entidade custava seis edições em lockstep: o tipo no
// Snapshot, o CREATE TABLE na migração, o laço de leitura, o laço de escrita, a
// string de DELETE e a lista de tabelas do teste. Aqui, cada entidade declara
// uma vez quais colunas ocupa e como converte — e leitura, escrita, a lista de
// DELETE e a cobertura do teste passam a derivar disso.
//
// Este módulo não conhece sql.js: fala em linhas (`Linha`) e parâmetros
// (`Valor`). Quem executa SQL é o adaptador.

import type { Snapshot, DestinoBaixa, RascunhoFechamento } from "../domain/snapshot.ts";

/** Um valor que o SQLite aceita como parâmetro de statement. */
export type Valor = string | number | null;

/** Uma linha lida do banco: coluna → valor cru. */
export type Linha = Record<string, unknown>;

/**
 * A unidade do mapeamento: quais colunas o campo ocupa, como escrevê-lo nelas e
 * como lê-lo de volta.
 *
 * Um campo não é obrigatoriamente uma coluna — `cicloFechamento` ocupa três
 * colunas de formatos diferentes (união de formas). Todos os campos nomeados
 * abaixo (`texto`, `booleano`, `csv`, `ciclo`…) são instâncias deste mesmo
 * contrato, então um caso novo e esquisito não cobra nada do núcleo: escreve-se
 * mais um campo.
 */
export interface Campo<T> {
  readonly colunas: readonly string[];
  escrever(valor: T): Valor[];
  ler(linha: Linha): T;
}

/** Um `Campo` para **cada** propriedade da entidade — esquecer uma não compila. */
export type CamposDe<E> = { [K in keyof E]-?: Campo<E[K]> };

export interface Tabela<E> {
  readonly nome: string;
  readonly campos: CamposDe<E>;
  /** Fragmento de `ORDER BY` na leitura, quando a ordem importa. */
  readonly ordem?: string;
}

export function tabela<E>(
  nome: string,
  campos: CamposDe<E>,
  ordem?: string,
): Tabela<E> {
  return ordem == null ? { nome, campos } : { nome, campos, ordem };
}

// ---------------------------------------------------------------- derivados --

const camposDe = <E>(t: Tabela<E>): [string, Campo<unknown>][] =>
  Object.entries(t.campos) as [string, Campo<unknown>][];

/** Colunas da tabela, na ordem em que os campos foram declarados. */
export function colunasDe<E>(t: Tabela<E>): string[] {
  return camposDe(t).flatMap(([, c]) => [...c.colunas]);
}

/** Valores de uma entidade, alinhados com `colunasDe`. */
export function paraLinha<E>(t: Tabela<E>, entidade: E): Valor[] {
  const e = entidade as Record<string, unknown>;
  return camposDe(t).flatMap(([k, c]) => c.escrever(e[k]));
}

/** A entidade reconstruída a partir de uma linha do banco. */
export function daLinha<E>(t: Tabela<E>, linha: Linha): E {
  const out: Record<string, unknown> = {};
  for (const [k, c] of camposDe(t)) out[k] = c.ler(linha);
  return out as E;
}

// ------------------------------------------------------------------- campos --

const str = (v: unknown): string => (typeof v === "string" ? v : String(v));

export const texto = (col: string): Campo<string> => ({
  colunas: [col],
  escrever: (v) => [v],
  ler: (l) => str(l[col]),
});

/** `texto` que carrega um union de string (`"saida" | "entrada"` etc.). */
export const textoComo = <T extends string>(col: string): Campo<T> => ({
  colunas: [col],
  escrever: (v) => [v],
  ler: (l) => str(l[col]) as T,
});

export const textoOpcional = (col: string): Campo<string | null> => ({
  colunas: [col],
  escrever: (v) => [v],
  ler: (l) => (l[col] == null ? null : str(l[col])),
});

/** `textoOpcional` que carrega um union de string (a `fonte` de uma Conta). */
export const textoComoOpcional = <T extends string>(col: string): Campo<T | null> => ({
  colunas: [col],
  escrever: (v) => [v],
  ler: (l) => (l[col] == null ? null : (str(l[col]) as T)),
});

export const inteiro = (col: string): Campo<number> => ({
  colunas: [col],
  escrever: (v) => [v],
  ler: (l) => Number(l[col]),
});

export const inteiroOpcional = (col: string): Campo<number | null> => ({
  colunas: [col],
  escrever: (v) => [v],
  ler: (l) => (l[col] == null ? null : Number(l[col])),
});

export const booleano = (col: string): Campo<boolean> => ({
  colunas: [col],
  escrever: (v) => [v ? 1 : 0],
  ler: (l) => Number(l[col]) !== 0,
});

/** Lista de textos numa coluna só, separada por vírgula. */
export const csv = (col: string): Campo<string[]> => ({
  colunas: [col],
  escrever: (v) => [v.join(",")],
  ler: (l) => str(l[col]).split(",").filter(Boolean),
});

/** Lista de números numa coluna só, separada por vírgula. */
export const csvNumeros = (col: string): Campo<number[]> => ({
  colunas: [col],
  escrever: (v) => [v.join(",")],
  ler: (l) => str(l[col]).split(",").filter(Boolean).map(Number),
});

/** Lista de textos opcional: `null` (NULL na coluna) é distinto de vazia. */
export const csvOpcional = (col: string): Campo<string[] | null> => ({
  colunas: [col],
  escrever: (v) => [v == null ? null : v.join(",")],
  ler: (l) => (l[col] == null ? null : str(l[col]).split(",").filter(Boolean)),
});

/** Campo serializado como JSON; volta ao `padrao` se o texto não abrir. */
export const json = <T>(col: string, padrao: () => T): Campo<T> => ({
  colunas: [col],
  escrever: (v) => [JSON.stringify(v)],
  ler: (l) => {
    try {
      return JSON.parse(str(l[col])) as T;
    } catch {
      return padrao();
    }
  },
});

/** Constrói um campo qualquer, para o que não cabe nos nomeados acima. */
export const campo = <T>(
  colunas: readonly string[],
  escrever: (valor: T) => Valor[],
  ler: (linha: Linha) => T,
): Campo<T> => ({ colunas, escrever, ler });

// --- campos de uma entidade só (ficam aqui perto do mapa, não na biblioteca) --

/** `CicloFechamento` — uma união de três formas em três colunas. */
const ciclo = (
  t: string,
  ini: string,
  fim: string,
): Campo<Snapshot["clientes"][number]["cicloFechamento"]> =>
  campo(
    [t, ini, fim],
    (c) =>
      c.tipo === "customizado"
        ? [c.tipo, c.diaInicio, c.diaFim]
        : [c.tipo, null, null],
    (l) => {
      const tipo = str(l[t]);
      if (tipo === "customizado") {
        return {
          tipo,
          diaInicio: Number(l[ini]) || 1,
          diaFim: Number(l[fim]) || 31,
        };
      }
      return { tipo: tipo === "quinzenal" ? "quinzenal" : "mensal" };
    },
  );

/** `Periodo` — duas datas em duas colunas. */
const periodo = (
  de: string,
  ate: string,
): Campo<Snapshot["faturas"][number]["periodo"]> =>
  campo(
    [de, ate],
    (p) => [p.de, p.ate],
    (l) => ({ de: str(l[de]), ate: str(l[ate]) }),
  );

// -------------------------------------------------------------------- mapa --

/** As chaves do Snapshot que são coleção de entidades — hoje, 22. */
export type ColecaoMapeada = {
  [K in keyof Snapshot]: Snapshot[K] extends readonly unknown[] ? K : never;
}[keyof Snapshot];

type Item<K extends ColecaoMapeada> =
  Snapshot[K] extends readonly (infer E)[] ? E : never;

/**
 * O mapa. A ordem das chaves é a ordem de `INSERT`; o `DELETE` roda na inversa.
 *
 * `config` e `listas` não estão aqui de propósito: não são coleções de
 * entidades (chave-valor e vocabulários nomeados) e o adaptador cuida delas à
 * mão. O tipo `ColecaoMapeada` já as exclui sozinho — não há lista de exceção
 * para manter.
 */
export type MapaTabelas = { [K in ColecaoMapeada]: Tabela<Item<K>> };

export const TABELAS: MapaTabelas = {
  perfis: tabela("perfil", {
    id: texto("id"),
    nome: texto("nome"),
    ativo: booleano("ativo"),
  }),
  usuarios: tabela("usuario", {
    id: texto("id"),
    nome: texto("nome"),
    login: textoOpcional("login"),
    senhaHash: textoOpcional("senha_hash"),
    perfilId: textoOpcional("perfil_id"),
    ativo: booleano("ativo"),
  }),
  permissoesModulo: tabela("permissao_modulo", {
    perfilId: texto("perfil_id"),
    modulo: textoComo("modulo"),
    nivel: textoComo("nivel"),
  }),
  categorias: tabela("categoria", {
    id: texto("id"),
    nome: texto("nome"),
    ativo: booleano("ativo"),
  }),
  tiposPeca: tabela("tipo_peca", {
    id: texto("id"),
    nome: texto("nome"),
    categoriaId: texto("categoria_id"),
    processos: csv("processos"),
    ativo: booleano("ativo"),
  }),
  precosBase: tabela(
    "preco_base",
    {
      tipoId: texto("tipo_id"),
      valorCentavos: inteiro("valor_centavos"),
      desde: texto("desde"),
    },
    "desde",
  ),
  precosCliente: tabela("preco_cliente", {
    clienteId: texto("cliente_id"),
    tipoId: texto("tipo_id"),
    valorCentavos: inteiro("valor_centavos"),
    desde: texto("desde"),
  }),
  clientes: tabela("cliente", {
    id: texto("id"),
    nome: texto("nome"),
    endereco: texto("endereco"),
    contato: texto("contato"),
    codigo: texto("codigo"),
    tipo: texto("tipo"),
    baseFaturamento: textoComo("base_faturamento"),
    cicloFechamento: ciclo("ciclo_tipo", "ciclo_dia_inicio", "ciclo_dia_fim"),
    devolucaoNoMesmoDia: booleano("devolucao_no_mesmo_dia"),
    tiposPecaVisiveis: csvOpcional("tipos_peca_visiveis"),
    diasVencimento: csvNumeros("dias_vencimento"),
    ativo: booleano("ativo"),
  }),
  paradas: tabela("parada", {
    id: texto("id"),
    clienteId: texto("cliente_id"),
    diasSemana: csvNumeros("dias_semana"),
    horario: texto("horario"),
    acao: textoComo("acao"),
  }),
  lotes: tabela("lote", {
    id: texto("id"),
    clienteId: texto("cliente_id"),
    codigo: texto("codigo"),
    os: inteiro("os"),
    coletaData: texto("coleta_data"),
    coletaHora: texto("coleta_hora"),
    paradaId: textoOpcional("parada_id"),
    sacos: inteiroOpcional("sacos"),
    urgente: booleano("urgente"),
    previstoManual: textoOpcional("previsto_manual"),
    relavagem: booleano("relavagem"),
    devolvidoEm: textoOpcional("devolvido_em"),
    devolvidoHora: textoOpcional("devolvido_hora"),
    canceladoEm: textoOpcional("cancelado_em"),
    motivoCancelamento: textoOpcional("motivo_cancelamento"),
    reabertoEm: textoOpcional("reaberto_em"),
    fechamentoAnteriorEm: textoOpcional("fechamento_anterior_em"),
    fechamentoAnteriorHora: textoOpcional("fechamento_anterior_hora"),
    fechamentoRegistradoEm: textoOpcional("fechamento_registrado_em"),
    rascunhoFechamento: json<RascunhoFechamento | null>("rascunho_fechamento", () => null),
  }),
  contagens: tabela("lote_contagem", {
    loteId: texto("lote_id"),
    tipoId: texto("tipo_id"),
    recebida: inteiro("recebida"),
    devolvida: inteiro("devolvida"),
    conferida: inteiroOpcional("conferida"),
    conferidaMotivo: textoOpcional("conferida_motivo"),
    retidaInformada: inteiroOpcional("retida_informada"),
  }),
  retencoes: tabela("retencao", {
    loteId: texto("lote_id"),
    tipoId: texto("tipo_id"),
    motivo: texto("motivo"),
    quantidade: inteiro("quantidade"),
  }),
  baixasBacklog: tabela("baixa_backlog", {
    loteId: texto("lote_id"),
    origemLoteId: texto("origem_lote_id"),
    tipoId: texto("tipo_id"),
    motivo: texto("motivo"),
    quantidade: inteiro("quantidade"),
    destino: textoComo<DestinoBaixa>("destino"),
  }),
  backlogInicial: tabela("backlog_inicial", {
    clienteId: texto("cliente_id"),
    tipoId: texto("tipo_id"),
    quantidade: inteiro("quantidade"),
  }),
  baixasBacklogInicial: tabela("baixa_backlog_inicial", {
    loteId: texto("lote_id"),
    clienteId: texto("cliente_id"),
    tipoId: texto("tipo_id"),
    quantidade: inteiro("quantidade"),
    destino: textoComo<DestinoBaixa>("destino"),
  }),
  perdas: tabela("perda", {
    id: texto("id"),
    clienteId: texto("cliente_id"),
    tipoId: texto("tipo_id"),
    origemLoteId: textoOpcional("origem_lote_id"),
    motivoOrigem: texto("motivo_origem"),
    motivo: texto("motivo"),
    quantidade: inteiro("quantidade"),
    data: texto("data"),
  }),
  faturas: tabela("fatura", {
    id: texto("id"),
    clienteId: texto("cliente_id"),
    periodo: periodo("periodo_de", "periodo_ate"),
    numero: textoOpcional("numero"),
    emitidaEm: textoOpcional("emitida_em"),
    ajustes: json("ajustes", () => []),
    compensacoes: json("compensacoes", () => []),
    loteIds: json("lote_ids", () => []),
    linhasCongeladas: json("linhas_congeladas", () => []),
    reaberturas: json("reaberturas", () => []),
    vencimento: textoOpcional("vencimento"),
  }),
  funcionarios: tabela("funcionario", {
    id: texto("id"),
    nome: texto("nome"),
    funcao: texto("funcao"),
    contato: texto("contato"),
    admissao: textoOpcional("admissao"),
    ativo: booleano("ativo"),
  }),
  insumos: tabela("insumo", {
    id: texto("id"),
    nome: texto("nome"),
    categoria: texto("categoria"),
    unidade: texto("unidade"),
    estoqueMinimo: inteiro("estoque_minimo"),
    fornecedor: textoOpcional("fornecedor"),
    marca: textoOpcional("marca"),
    ativo: booleano("ativo"),
  }),
  categoriasFinanceiras: tabela("categoria_financeira", {
    id: texto("id"),
    nome: texto("nome"),
    subcategoria: texto("subcategoria"),
    aplicaA: textoComo("aplica_a"),
    sistema: booleano("sistema"),
    ativo: booleano("ativo"),
  }),
  contas: tabela("conta", {
    id: texto("id"),
    direcao: textoComo("direcao"),
    descricao: texto("descricao"),
    valorCentavos: inteiro("valor_centavos"),
    avulsoCentavos: inteiroOpcional("avulso_centavos"),
    vencimento: texto("vencimento"),
    categoriaId: texto("categoria_id"),
    fonte: textoComoOpcional("fonte"),
    clienteId: textoOpcional("cliente_id"),
    faturaId: textoOpcional("fatura_id"),
    fornecedor: textoOpcional("fornecedor"),
    observacao: texto("observacao"),
    itens: json("itens", () => []),
  }),
  pagamentos: tabela("pagamento", {
    id: texto("id"),
    contaId: texto("conta_id"),
    data: texto("data"),
    valorCentavos: inteiro("valor_centavos"),
  }),
  responsaveis: tabela("responsavel", {
    id: texto("id"),
    nome: texto("nome"),
    limiteMensalCentavos: inteiroOpcional("limite_mensal_centavos"),
    ativo: booleano("ativo"),
  }),
  retiradasPessoais: tabela("retirada_pessoal", {
    id: texto("id"),
    responsavelId: texto("responsavel_id"),
    data: texto("data"),
    valorCentavos: inteiro("valor_centavos"),
  }),
  temposReferencia: tabela("tempo_referencia", {
    etapa: textoComo("etapa"),
    pecas: inteiro("pecas"),
    minutos: inteiro("minutos"),
  }),
};

/** As coleções mapeadas, na ordem de `INSERT`. O `DELETE` roda na inversa. */
export const MAPEADAS = Object.keys(TABELAS) as ColecaoMapeada[];

/**
 * Tabelas que existem no banco mas não estão no mapa, porque não guardam
 * coleções de entidades: `config` é chave-valor e `lista_valor` guarda os
 * vocabulários nomeados. O adaptador cuida das duas à mão — e o teste de
 * cobertura usa esta lista para saber que a ausência é proposital.
 */
export const FORA_DO_MAPA = ["config", "lista_valor"] as const;

/**
 * Tabelas que o Snapshot **não gerencia**: existem no banco, mas não são lidas
 * pelo `loadSnapshot` nem regravadas pelo `writeSnapshot`. Hoje só o
 * `log_auditoria` (append-only, escrito pelo servidor). O teste de cobertura
 * usa esta lista para saber que a ausência do mapa é proposital.
 */
export const NAO_GERENCIADAS = ["log_auditoria", "fatura_pago_legado"] as const;

/** Toda tabela que o `writeSnapshot` regrava, na ordem de `DELETE`. */
export function tabelasParaLimpar(): string[] {
  const mapeadas = [...MAPEADAS]
    .reverse()
    .map((k) => (TABELAS[k] as Tabela<unknown>).nome);
  return [...mapeadas, ...FORA_DO_MAPA];
}
